// Title-screen key art: the board plate (a still of the real Ministry at dusk, `npm run backdrop`)
// with live layers on top — a fan of real cards with one being cast, the Stamp of Precedence coming
// down on a form, paperwork and motes drifting through the air. Everything is DOM and CSS on the
// compositor: no WebGL, no per-frame work beyond a throttled pointer parallax.
//
// The art is laid out on a fixed 1920×1080 stage that is scaled to cover the screen (like the plate
// image itself), so every layer stays registered to the board whatever the window shape.
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Color } from '@bcm/rules';
import { Card } from './Card';
import { ManaCrystal, PermitToken } from './Icon';
import { WaxSealArt } from './WaxSeal';
import { audio } from '../audio/sfx';
import menuImage from '../assets/menu/backdrop.webp';
import lobbyImage from '../assets/menu/backdrop-centered.webp';
import stampImage from '../assets/menu/stamp.webp';
import '../styles/keyart.css';

const W = 1920;
const H = 1080;
const REDUCED = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/** A small deterministic random, so the motes are the same on every visit. */
function rng(seed: number) {
  return () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
}

interface Mote {
  x: number;
  y: number;
  size: number;
  dur: number;
  delay: number;
  color: string;
}

function motes(n: number, seed: number, region: [number, number, number, number]): Mote[] {
  const r = rng(seed);
  const colors = ['#ffd98a', '#ffd98a', '#ffe9b8', '#f26bbd', '#8fc2ff'];
  return Array.from({ length: n }, () => ({
    x: region[0] + r() * (region[2] - region[0]),
    y: region[1] + r() * (region[3] - region[1]),
    size: 2 + r() * 4,
    dur: 9 + r() * 10,
    delay: -r() * 18,
    color: colors[Math.floor(r() * colors.length)]!,
  }));
}

type Float = { kind: 'form' | 'permit' | 'mana'; x: number; y: number; s: number; rot: number; dur: number; delay: number; color?: Color; far?: boolean };

const FLOATS_MENU: Float[] = [
  { kind: 'form', x: 820, y: 70, s: 1, rot: -14, dur: 19, delay: -3 },
  { kind: 'form', x: 1500, y: 150, s: 0.8, rot: 12, dur: 23, delay: -11, far: true },
  { kind: 'form', x: 1790, y: 380, s: 1.1, rot: -6, dur: 17, delay: -7 },
  { kind: 'permit', x: 1130, y: 90, s: 1, rot: 10, dur: 15, delay: -2, color: 'red' },
  { kind: 'permit', x: 1690, y: 60, s: 0.85, rot: -18, dur: 18, delay: -9, color: 'blue', far: true },
  { kind: 'permit', x: 640, y: 330, s: 0.9, rot: 22, dur: 16, delay: -5, color: 'yellow', far: true },
  { kind: 'permit', x: 1330, y: 300, s: 0.75, rot: -8, dur: 21, delay: -14, color: 'green', far: true },
  { kind: 'mana', x: 1010, y: 250, s: 1, rot: 8, dur: 14, delay: -6 },
  { kind: 'mana', x: 1850, y: 200, s: 1.2, rot: -12, dur: 20, delay: -1 },
];
const FLOATS_LOBBY: Float[] = [
  { kind: 'form', x: 120, y: 160, s: 1, rot: -14, dur: 19, delay: -3 },
  { kind: 'form', x: 1700, y: 120, s: 0.9, rot: 12, dur: 23, delay: -11, far: true },
  { kind: 'permit', x: 300, y: 760, s: 0.9, rot: 10, dur: 15, delay: -2, color: 'red', far: true },
  { kind: 'permit', x: 1640, y: 820, s: 1, rot: -18, dur: 18, delay: -9, color: 'blue' },
  { kind: 'mana', x: 1780, y: 520, s: 1.1, rot: 8, dur: 14, delay: -6 },
  { kind: 'mana', x: 160, y: 480, s: 0.9, rot: -8, dur: 20, delay: -1, far: true },
];

/** Cards in the foreground fan (bottom right, over the dark table in front of the board). */
const FAN: { def: string; x: number; y: number; rot: number; z: number; scale?: number }[] = [
  { def: 'problem.cacophonous_clocktower', x: 1300, y: 800, rot: -18, z: 1, scale: 0.76 },
  { def: 'spell.form_g77_golem', x: 1405, y: 812, rot: -13, z: 2, scale: 0.82 },
  { def: 'spell.lead_to_gold_ledger', x: 1535, y: 790, rot: -3, z: 3, scale: 0.82 },
  { def: 'spell.oracles_omnibus', x: 1665, y: 818, rot: 8, z: 4, scale: 0.82 },
  { def: 'decree.open_plan_office', x: 1790, y: 868, rot: 18, z: 3, scale: 0.78 },
];
/** The card being cast: lifted out of the fan, glowing in its school's colour. */
const CAST = { def: 'spell.thunderclap_filing', x: 1590, y: 560, rot: 5, scale: 0.88 };

export function KeyArt({ centered, onReady }: { centered: boolean; onReady: () => void }) {
  const stage = useRef<HTMLDivElement>(null);
  const img = useRef<HTMLImageElement>(null);
  const [ready, setReady] = useState(false);
  const [box, setBox] = useState({ k: 1, x: 0, y: 0 });

  // Cover the window with the 1920×1080 stage; keep the board (right of the menu) in view.
  useEffect(() => {
    const fit = () => {
      const k = Math.max(innerWidth / W, innerHeight / H);
      const bias = centered ? 0.5 : 0.68;
      // Wide windows crop top and bottom: favour the bottom (the cards and the stamp); the top is table.
      setBox({ k, x: (innerWidth - W * k) * bias, y: (innerHeight - H * k) * (centered ? 0.5 : 0.8) });
    };
    fit();
    addEventListener('resize', fit);
    return () => removeEventListener('resize', fit);
  }, [centered]);

  // A gentle parallax: layers shift with the pointer, each by its own depth.
  useEffect(() => {
    if (REDUCED) return;
    let raf = 0;
    let px = 0;
    let py = 0;
    const move = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      px = (e.clientX / innerWidth) * 2 - 1;
      py = (e.clientY / innerHeight) * 2 - 1;
      if (!raf)
        raf = requestAnimationFrame(() => {
          raf = 0;
          stage.current?.style.setProperty('--mx', px.toFixed(3));
          stage.current?.style.setProperty('--my', py.toFixed(3));
        });
    };
    addEventListener('pointermove', move, { passive: true });
    return () => {
      removeEventListener('pointermove', move);
      cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    // A cached image can finish loading before React attaches onLoad.
    if (img.current?.complete) setReady(true);
  }, []);
  useEffect(() => {
    if (!ready) return;
    onReady();
    // The stamp lands (see .ka-stamp): a soft thump if audio is already unlocked (back from a game).
    if (!centered && !REDUCED) {
      const t = window.setTimeout(() => audio.play('place', { pitch: 0.55, gain: 0.7 }), 1450);
      return () => clearTimeout(t);
    }
  }, [ready, centered, onReady]);

  const dust = useMemo(() => motes(centered ? 26 : 34, centered ? 7 : 3, [0, 0, W, H]), [centered]);
  const sparks = useMemo(() => motes(12, 11, [CAST.x + 20, CAST.y + 40, CAST.x + 220, CAST.y + 300]), []);
  const floats = centered ? FLOATS_LOBBY : FLOATS_MENU;

  return (
    <div
      ref={stage}
      className={`ka-stage ${ready ? 'ready' : ''} ${centered ? 'ka-lobby' : 'ka-menu'}`}
      style={{ width: W, height: H, transform: `translate(${box.x}px, ${box.y}px) scale(${box.k})` }}
      aria-hidden
    >
      <div className="ka-layer" style={{ '--depth': 0.35 } as React.CSSProperties}>
        <img ref={img} className="ka-plate" src={centered ? lobbyImage : menuImage} alt="" decoding="async" onLoad={() => setReady(true)} />
      </div>

      <div className="ka-layer" style={{ '--depth': 0.7 } as React.CSSProperties}>
        {floats.map((f, i) => (
          <div
            key={i}
            className={`ka-float ${f.far ? 'far' : ''}`}
            style={{ left: f.x, top: f.y, '--s': f.s, '--rot': `${f.rot}deg`, '--dur': `${f.dur}s`, '--delay': `${f.delay}s` } as React.CSSProperties}
          >
            {f.kind === 'form' ? <MiniForm /> : f.kind === 'permit' ? <PermitToken color={f.color!} size={46} /> : <ManaCrystal size={44} />}
          </div>
        ))}
      </div>

      {!centered && (
        <>
          <div className="ka-layer" style={{ '--depth': 1 } as React.CSSProperties}>
            <div className="ka-desk">
              <div className="ka-form">
                <div className="ka-form-head">Ministry of Arcane Affairs</div>
                <div className="ka-form-title">Form P-11 · Precedence</div>
                {Array.from({ length: 7 }, (_, i) => (
                  <div key={i} className="ka-form-line" style={{ width: `${[92, 78, 88, 64, 90, 72, 50][i]}%` }} />
                ))}
                <div className="ka-form-sign">Signed, the Clerk of the Stamp</div>
                <div className="ka-form-seal">
                  <WaxSealArt size={118} />
                </div>
              </div>
              <div className="ka-stamp-shadow" />
            </div>
            <img className="ka-stamp" src={stampImage} alt="" />
          </div>

          <div className="ka-layer" style={{ '--depth': 1.35 } as React.CSSProperties}>
            {FAN.map((c, i) => (
              <div
                key={c.def}
                className="ka-card"
                style={{ left: c.x, top: c.y, zIndex: c.z, '--rot': `${c.rot}deg`, '--s': c.scale ?? 1, '--i': i } as React.CSSProperties}
              >
                <div className="ka-bob">
                  <Card def={c.def} size="lg" preview={false} />
                </div>
              </div>
            ))}
            <div className="ka-card cast" style={{ left: CAST.x, top: CAST.y, zIndex: 6, '--rot': `${CAST.rot}deg`, '--s': CAST.scale, '--i': 5 } as React.CSSProperties}>
              <div className="ka-bob">
                <Card def={CAST.def} size="lg" preview={false} />
              </div>
            </div>
            {sparks.map((m, i) => (
              <span key={i} className="ka-spark" style={{ left: m.x, top: m.y, width: m.size + 2, height: m.size + 2, '--dur': `${m.dur * 0.35}s`, '--delay': `${m.delay * 0.35}s` } as React.CSSProperties} />
            ))}
          </div>
        </>
      )}

      <div className="ka-layer" style={{ '--depth': 1.7 } as React.CSSProperties}>
        {dust.map((m, i) => (
          <span
            key={i}
            className="ka-mote"
            style={{ left: m.x, top: m.y, width: m.size, height: m.size, color: m.color, background: 'currentColor', '--dur': `${m.dur}s`, '--delay': `${m.delay}s` } as React.CSSProperties}
          />
        ))}
      </div>
    </div>
  );
}

/** A little floating form: parchment, a header rule and a few lines of text. */
function MiniForm() {
  return (
    <div className="ka-miniform">
      <i />
      <i />
      <i />
      <i />
      <b />
    </div>
  );
}
